import React, { useState } from "react";
import { Link, Outlet, useLocation, useResolvedPath } from "react-router-dom";
import { DownArrowIcon, SitemapIcon, UserGroupIcon } from "@wexinc/wex-ui";
import classNames from "classnames";
import { CopAdminRoutes, CopRoutes } from "app/routes";
import { useCopSecurityService } from "services/security";

const AdminLayout: React.FC = () => {
  return (
    <div className="flex overflow-auto h-full">
      <div className="min-w-[224px] pt-20 bg-neutral-dark-5 border-r border-r-neutral-dark-20 ">
        <LeftNav />
      </div>
      <div
        className="pt-16 relative h-full overflow-auto"
        style={{ width: "calc(100% - 224px)" }}
      >
        <Outlet />
      </div>
    </div>
  );
};

const LeftNav: React.FC = () => {
  const securityService = useCopSecurityService();
  const canAccessUsersList =
    securityService.userHasGlobalPermission("editAccountManager") ||
    securityService.userHasGlobalPermission("editSupport") ||
    securityService.userHasGlobalPermission("editRoles");

  const leftNavData = [
    {
      text: "Organizations",
      icon: <SitemapIcon className="mr-4 fill-current" />,
      to: CopAdminRoutes.organizations,
    },
    canAccessUsersList && {
      text: "Users",
      icon: <UserGroupIcon className="mr-4 fill-current" />,
      to: CopAdminRoutes.users,
    },
  ];

  return (
    <ul className="pl-1">
      <li className="px-4 pb-4">
        <Link
          to={CopRoutes.root}
          className="flex items-center"
        >
          <DownArrowIcon className="mr-4 rotate-90" />
          COP Hub
        </Link>
      </li>
      {leftNavData.map((i, index) => {
        if (i) {
          return (
            <li
              key={i.to}
              className="mr-4 mb-1"
            >
              <LeftNavItem
                {...i}
                dataTestId={`menu-item-${index}`}
              />
            </li>
          );
        }
      })}
    </ul>
  );
};

const LeftNavItem: React.FC<{
  text: string;
  icon: React.ReactElement;
  to: string;
  dataTestId: string;
}> = (props) => {
  const [isFocused, setIsFocused] = useState(false);
  const location = useLocation();
  const resolved = useResolvedPath(props.to);
  const isSelected = location.pathname.indexOf(resolved.pathname) !== -1;

  return (
    <Link
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      data-test-id={props.dataTestId}
      to={props.to}
      className={classNames(
        "focus:outline-none h-10 inline-block w-full rounded-r-lg",
        isSelected ? "bg-primary-10 text-neutral-dark-100 font-semibold" : "text-neutral-dark-70",
        isFocused ? "shadow-focus-1" : "",
      )}
    >
      <label className="flex items-center h-full relative pl-5 cursor-pointer">
        <div className={classNames("absolute w-1 h-full left-0", isSelected ? "bg-primary-50" : "")} />
        <span className="text-primary-50 fill-current">{props.icon}</span>
        {props.text}
      </label>
    </Link>
  );
};

export default AdminLayout;
